import React, { createContext, useContext, useEffect, useState } from "react";
import PermissionApi, { TestPlanPermissions } from "../api/services/PermissionApi";
import ErrorSnackbarContext from "./ErrorSnackbarContext";
import CustomLoading from "../components/Alert/CustomLoading";
import useEnv from "../hooks/env";

export const TestPlanPermissionContext = createContext<
  TestPlanPermissions & {
    refreshing: boolean;
    initiallyLoaded: boolean;
    updatePermissionState: () => Promise<void>;
  }
>({
  updatePermissionState: () => Promise.resolve(),
  refreshing: false,
  initiallyLoaded: false,
});

export const TestPlanPermissionContextProvider: React.FunctionComponent<{
  testRequestId: string;
}> = (props: React.PropsWithChildren<{ testRequestId: string }>) => {
  const { api } = useEnv();
  const permissionApi = new PermissionApi(api);
  const [refreshing, setRefreshing] = useState(false);
  const [initiallyLoaded, setInitiallyLoaded] = useState(false);
  const [permissions, setPermissions] = useState<TestPlanPermissions>({});
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { children, testRequestId } = props;

  const updatePermissionState = () => {
    if (testRequestId === "new") {
      return Promise.resolve().then(() => {
        setPermissions({});
      });
    }
    return permissionApi.fetchTestPlanPermissions(testRequestId).then((response) => {
      setPermissions(response.data);
    });
  };

  const load = () => {
    setRefreshing(false);
    updatePermissionState()
      .catch(handleHttpError)
      .finally(() => {
        setRefreshing(true);
        setInitiallyLoaded(true);
      });
  };

  useEffect(() => {
    load();
  }, [testRequestId]);

  return (
    <TestPlanPermissionContext.Provider
      value={{
        ...permissions,
        initiallyLoaded,
        refreshing,
        updatePermissionState,
      }}
    >
      {!initiallyLoaded && <CustomLoading />}
      {initiallyLoaded && children}
    </TestPlanPermissionContext.Provider>
  );
};

export default TestPlanPermissionContext;
